import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import {
  Plus,
  Pencil,
  Trash,
  Storefront,
  MagnifyingGlass,
  Spinner,
  Phone,
  Envelope,
  MapPin
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const COUNTRIES = [
  'Antigua and Barbuda', 'Bahamas', 'Barbados', 'Belize', 'Dominica',
  'Grenada', 'Guyana', 'Haiti', 'Jamaica', 'Saint Kitts and Nevis',
  'Saint Lucia', 'Saint Vincent and the Grenadines', 'Suriname',
  'Trinidad and Tobago', 'United States', 'Other'
];

const PRODUCT_CATEGORIES = [
  'Fresh Produce', 'Seafood', 'Dairy', 'Beverages', 'Frozen Foods',
  'Dry Goods', 'Linens', 'Toiletries', 'Cleaning Supplies', 'Kitchen Equipment',
  'Furniture', 'Electronics', 'Office Supplies', 'Other'
];

const VendorsPage = () => {
  const { getAuthHeaders } = useAuth();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    company_name: '',
    contact_person: '',
    email: '',
    phone: '',
    country: '',
    product_categories: [],
    payment_terms: 'Net 30',
    lead_time_days: 14,
    preferred_currency: 'USD'
  });

  useEffect(() => {
    fetchVendors();
  }, []);

  const fetchVendors = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/vendors`, getAuthHeaders());
      setVendors(response.data);
    } catch (error) {
      toast.error('Failed to load vendors');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingVendor) {
        await axios.put(`${API_URL}/api/vendors/${editingVendor.id}`, formData, getAuthHeaders());
        toast.success('Vendor updated successfully');
      } else {
        await axios.post(`${API_URL}/api/vendors`, formData, getAuthHeaders());
        toast.success('Vendor created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchVendors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save vendor');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (vendor) => {
    if (!confirm(`Are you sure you want to deactivate ${vendor.company_name}?`)) return;
    try {
      await axios.delete(`${API_URL}/api/vendors/${vendor.id}`, getAuthHeaders());
      toast.success('Vendor deactivated');
      fetchVendors();
    } catch (error) {
      toast.error('Failed to deactivate vendor');
    }
  };

  const openEdit = (vendor) => {
    setEditingVendor(vendor);
    setFormData({
      company_name: vendor.company_name,
      contact_person: vendor.contact_person,
      email: vendor.email,
      phone: vendor.phone,
      country: vendor.country,
      product_categories: vendor.product_categories,
      payment_terms: vendor.payment_terms,
      lead_time_days: vendor.lead_time_days,
      preferred_currency: vendor.preferred_currency
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingVendor(null);
    setFormData({
      company_name: '',
      contact_person: '',
      email: '',
      phone: '',
      country: '',
      product_categories: [],
      payment_terms: 'Net 30',
      lead_time_days: 14,
      preferred_currency: 'USD'
    });
  };

  const toggleCategory = (category) => {
    setFormData(prev => ({
      ...prev,
      product_categories: prev.product_categories.includes(category)
        ? prev.product_categories.filter(c => c !== category)
        : [...prev.product_categories, category]
    }));
  };

  const filteredVendors = vendors.filter(v =>
    v.company_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    v.country.toLowerCase().includes(searchQuery.toLowerCase()) ||
    v.product_categories.some(c => c.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#0F52BA] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Vendor Database
          </h1>
          <p className="text-slate-500 mt-1">Manage your supplier registry</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#0F52BA] hover:bg-[#0A3D8F]" data-testid="new-vendor-btn">
              <Plus size={20} className="mr-2" />
              Add Vendor
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>
                {editingVendor ? 'Edit Vendor' : 'Add New Vendor'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Company Name</Label>
                  <Input
                    value={formData.company_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
                    placeholder="Caribbean Foods Ltd"
                    required
                    data-testid="vendor-company-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Contact Person</Label>
                  <Input
                    value={formData.contact_person}
                    onChange={(e) => setFormData(prev => ({ ...prev, contact_person: e.target.value }))}
                    placeholder="John Smith"
                    required
                    data-testid="vendor-contact-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="contact@vendor.com"
                    required
                    data-testid="vendor-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="+1-268-555-0100"
                    required
                    data-testid="vendor-phone-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Country</Label>
                <Select
                  value={formData.country}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, country: value }))}
                >
                  <SelectTrigger data-testid="vendor-country-select">
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map(country => (
                      <SelectItem key={country} value={country}>{country}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Product Categories</Label>
                <div className="flex flex-wrap gap-2">
                  {PRODUCT_CATEGORIES.map(category => (
                    <button
                      key={category}
                      type="button"
                      onClick={() => toggleCategory(category)}
                      className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                        formData.product_categories.includes(category)
                          ? 'bg-[#0F52BA] text-white'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                      data-testid={`category-${category.toLowerCase().replace(/ /g, '-')}`}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Payment Terms</Label>
                  <Select
                    value={formData.payment_terms}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, payment_terms: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Net 15">Net 15</SelectItem>
                      <SelectItem value="Net 30">Net 30</SelectItem>
                      <SelectItem value="Net 45">Net 45</SelectItem>
                      <SelectItem value="Net 60">Net 60</SelectItem>
                      <SelectItem value="Due on Receipt">Due on Receipt</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Lead Time (days)</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.lead_time_days}
                    onChange={(e) => setFormData(prev => ({ ...prev, lead_time_days: parseInt(e.target.value) || 14 }))}
                    data-testid="vendor-leadtime-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Currency</Label>
                  <Select
                    value={formData.preferred_currency}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, preferred_currency: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD ($)</SelectItem>
                      <SelectItem value="XCD">XCD (EC$)</SelectItem>
                      <SelectItem value="JMD">JMD (J$)</SelectItem>
                      <SelectItem value="TTD">TTD (TT$)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                <Button type="button" variant="secondary" onClick={() => { setDialogOpen(false); resetForm(); }}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting} data-testid="save-vendor-btn">
                  {submitting ? <Spinner size={20} className="animate-spin mr-2" /> : null}
                  {editingVendor ? 'Update Vendor' : 'Add Vendor'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          placeholder="Search vendors..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="vendor-search-input"
        />
      </div>

      {/* Vendors grid */}
      {filteredVendors.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-16 text-center">
          <Storefront size={48} className="mx-auto text-slate-300 mb-4" />
          <p className="text-slate-500">No vendors found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredVendors.map(vendor => (
            <VendorCard
              key={vendor.id}
              vendor={vendor}
              onEdit={() => openEdit(vendor)}
              onDelete={() => handleDelete(vendor)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const VendorCard = ({ vendor, onEdit, onDelete }) => {
  const currencySymbols = { USD: '$', XCD: 'EC$', JMD: 'J$', TTD: 'TT$' };
  
  return (
    <div 
      className="bg-white rounded-xl border border-slate-100 shadow-sm p-6 card-hover"
      data-testid={`vendor-card-${vendor.id}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="w-12 h-12 rounded-xl bg-[#38B2AC]/10 flex items-center justify-center">
          <Storefront size={24} weight="duotone" className="text-[#38B2AC]" />
        </div>
        <div className="flex gap-1">
          <button
            onClick={onEdit}
            className="p-2 rounded-lg text-slate-400 hover:text-[#0F52BA] hover:bg-[#F0F7FF] transition-colors"
            data-testid={`edit-vendor-${vendor.id}`}
          >
            <Pencil size={18} />
          </button>
          <button
            onClick={onDelete}
            className="p-2 rounded-lg text-slate-400 hover:text-[#E63946] hover:bg-red-50 transition-colors"
            data-testid={`delete-vendor-${vendor.id}`}
          >
            <Trash size={18} />
          </button>
        </div>
      </div>

      <h3 className="font-semibold text-slate-800 mb-1">{vendor.company_name}</h3>
      <p className="text-sm text-slate-500 mb-4">{vendor.contact_person}</p>

      <div className="space-y-2 text-sm">
        <div className="flex items-center gap-2 text-slate-500">
          <MapPin size={16} />
          <span>{vendor.country}</span>
        </div>
        <div className="flex items-center gap-2 text-slate-500">
          <Phone size={16} />
          <span>{vendor.phone}</span>
        </div>
        <div className="flex items-center gap-2 text-slate-500">
          <Envelope size={16} />
          <span className="truncate">{vendor.email}</span>
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-slate-100">
        <div className="flex flex-wrap gap-1 mb-3">
          {vendor.product_categories.slice(0, 3).map(cat => (
            <span key={cat} className="px-2 py-0.5 bg-slate-100 rounded text-xs text-slate-600">
              {cat}
            </span>
          ))}
          {vendor.product_categories.length > 3 && (
            <span className="px-2 py-0.5 bg-slate-100 rounded text-xs text-slate-600">
              +{vendor.product_categories.length - 3}
            </span>
          )}
        </div>
        <div className="flex items-center justify-between text-xs text-slate-500">
          <span>{vendor.payment_terms}</span>
          <span>{vendor.lead_time_days} days lead time</span>
          <span>{currencySymbols[vendor.preferred_currency] || vendor.preferred_currency}</span>
        </div>
      </div>
    </div>
  );
};

export default VendorsPage;
